import type { Decimal } from '@greencandle/api-client'
import { formatMoney, formatPercent, formatQuantity, signOf } from '@greencandle/api-client'

/**
 * A number the API may not have been able to produce.
 *
 * The backend is careful to return null rather than zero when a price or an FX rate is missing,
 * and to say why in a companion note. That care is wasted if the UI renders null as a dash, so
 * an unknown figure is visibly different from a real one and carries its reason (NFR-U4).
 */
export function Unknown({ reason }: { reason?: string | null }) {
  return (
    <span className="unknown" title={reason ?? 'Not available'}>
      not available
    </span>
  )
}

export function Money({
  value,
  currency,
  reason,
  signed = false,
  display = 'num',
}: {
  value: Decimal | null | undefined
  currency: string
  reason?: string | null
  signed?: boolean
  /**
   * `num` is tabular, for columns that must align vertically. `figure` is proportional,
   * for a headline value - equal-width digits make a large number look loose.
   */
  display?: 'num' | 'figure'
}) {
  const text = formatMoney(value, currency)
  if (text === null) {
    return <Unknown reason={reason} />
  }
  const direction = signed ? signOf(value) : null
  const tone = direction === 'up' ? ' up' : direction === 'down' ? ' down' : ''
  const className = `${display}${tone}`
  // A leading + on gains, so the sign is legible without relying on colour.
  const prefix = signed && direction === 'up' ? '+' : ''
  return (
    <span className={className}>
      {prefix}
      {text}
    </span>
  )
}

export function Percent({
  value,
  reason,
  signed = true,
  display = 'num',
}: {
  value: Decimal | null | undefined
  reason?: string | null
  signed?: boolean
  display?: 'num' | 'figure'
}) {
  const text = formatPercent(value)
  if (text === null) {
    return <Unknown reason={reason} />
  }
  const direction = signed ? signOf(value) : null
  const tone = direction === 'up' ? ' up' : direction === 'down' ? ' down' : ''
  const className = `${display}${tone}`
  const prefix = signed && direction === 'up' ? '+' : ''
  return (
    <span className={className}>
      {prefix}
      {text}
    </span>
  )
}

export function Quantity({ value }: { value: Decimal | null | undefined }) {
  const text = formatQuantity(value)
  return text === null ? <Unknown /> : <span className="num">{text}</span>
}
